import React, { useState, useEffect } from 'react';
import { Plus, X, Building2, CreditCard, TrendingUp, FileText, Briefcase, Loader2 } from 'lucide-react';

interface BankAccount {
  id: string;
  bankName: string;
  accountTitle: string;
  accountNumber: string;
  currency: 'USD' | 'CDF' | 'EUR';
  accountType: 'checking' | 'savings' | 'business';
}

interface Loan {
  id: string;
  purpose: string;
  totalAmount: number;
  outstandingAmount: number;
  currency: 'USD' | 'CDF' | 'EUR';
  lender: string;
  startDate: string;
  endDate: string;
  interestRate: number;
  monthlyPayment: number;
}

interface FundingRound {
  id: string;
  roundType: 'pre-seed' | 'seed' | 'series-a' | 'series-b' | 'series-c' | 'bridge';
  amountRaised: number;
  currency: 'USD' | 'CDF' | 'EUR';
  preMoney: number;
  postMoney: number;
  investors: string[];
  date: string;
  useOfFunds: string;
}

interface FinancialInputProps {
  label: string;
  type: 'bank-accounts' | 'loans' | 'funding-rounds';
  value: any[];
  onSave: (data: any[]) => Promise<void>;
  disabled?: boolean;
  companyType?: 'traditional' | 'startup';
}

const FinancialInput: React.FC<FinancialInputProps> = ({
  label,
  type,
  value = [],
  onSave,
  disabled = false,
  companyType
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [items, setItems] = useState(value);
  const [newItem, setNewItem] = useState<any>({});

  // ✨ SYNC: Synchroniser items quand value change depuis le parent
  // IMPORTANT: Copie profonde pour éviter les mutations croisées entre instances
  useEffect(() => {
    if (!isEditing) {
      const copiedItems = (value || []).map(item => ({ ...item }));
      setItems(copiedItems);
    }
  }, [value, isEditing, type]);

  const currencies = [
    { value: 'USD', label: 'Dollar US ($)' },
    { value: 'CDF', label: 'Franc congolais (FC)' },
    { value: 'EUR', label: 'Euro (€)' }
  ];

  const roundTypes = [
    { value: 'pre-seed', label: 'Pré-amorçage' },
    { value: 'seed', label: 'Amorçage' },
    { value: 'series-a', label: 'Série A' },
    { value: 'series-b', label: 'Série B' },
    { value: 'series-c', label: 'Série C' },
    { value: 'bridge', label: 'Bridge/Pont' }
  ];

  const accountTypes = [
    { value: 'checking', label: 'Compte courant' },
    { value: 'savings', label: 'Compte épargne' },
    { value: 'business', label: 'Compte entreprise' }
  ];

  const handleStartEditing = () => {
    setIsEditing(true);
    // IMPORTANT: Copie profonde
    const copiedItems = (value || []).map(item => ({ ...item }));
    setItems(copiedItems);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    // IMPORTANT: Copie profonde pour restaurer
    const copiedItems = (value || []).map(item => ({ ...item }));
    setItems(copiedItems);
    setNewItem({});
  };

  const handleSaveEditing = async () => {
    try {
      setIsSaving(true);
      // IMPORTANT: Copie profonde pour la sauvegarde
      const itemsToSave = items.map(item => ({ ...item }));
      await onSave(itemsToSave);
      setIsEditing(false);
      setNewItem({});
    } catch (error) {
      console.error('Erreur lors de la sauvegarde financière:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddItem = () => {
    if (type === 'bank-accounts' && newItem.bankName && newItem.accountNumber) {
      const account: BankAccount = {
        id: `bank_${Date.now()}`,
        bankName: newItem.bankName,
        accountTitle: newItem.accountTitle || '',
        accountNumber: newItem.accountNumber,
        currency: newItem.currency || 'USD',
        accountType: newItem.accountType || 'business'
      };
      setItems([...items, account]);
      setNewItem({});
    } else if (type === 'loans' && newItem.purpose && newItem.totalAmount) {
      const loan: Loan = {
        id: `loan_${Date.now()}`,
        purpose: newItem.purpose,
        totalAmount: parseFloat(newItem.totalAmount) || 0,
        outstandingAmount: parseFloat(newItem.outstandingAmount) || 0,
        currency: newItem.currency || 'USD',
        lender: newItem.lender || '',
        startDate: newItem.startDate || '',
        endDate: newItem.endDate || '',
        interestRate: parseFloat(newItem.interestRate) || 0,
        monthlyPayment: parseFloat(newItem.monthlyPayment) || 0
      };
      setItems([...items, loan]);
      setNewItem({});
    } else if (type === 'funding-rounds' && newItem.roundType && newItem.amountRaised) {
      const round: FundingRound = {
        id: `round_${Date.now()}`,
        roundType: newItem.roundType,
        amountRaised: parseFloat(newItem.amountRaised) || 0,
        currency: newItem.currency || 'USD',
        preMoney: parseFloat(newItem.preMoney) || 0,
        postMoney: parseFloat(newItem.postMoney) || 0,
        investors: newItem.investors ? newItem.investors.split(',').map((inv: string) => inv.trim()) : [],
        date: newItem.date || '',
        useOfFunds: newItem.useOfFunds || ''
      };
      setItems([...items, round]);
      setNewItem({});
    }
  };

  const handleRemoveItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };

  const getIcon = () => {
    switch (type) {
      case 'bank-accounts': return <Building2 className="w-5 h-5 text-[#197ca8]" />;
      case 'loans': return <CreditCard className="w-5 h-5 text-[#197ca8]" />;
      case 'funding-rounds': return <TrendingUp className="w-5 h-5 text-[#197ca8]" />;
      default: return <FileText className="w-5 h-5 text-[#197ca8]" />;
    }
  };

  const getContextualLabel = () => {
    if (type === 'bank-accounts') {
      return companyType === 'startup' ? 'Comptes Bancaires et Fintech' : 'Références Bancaires';
    }
    if (type === 'loans') {
      return companyType === 'startup' ? 'Dettes et Obligations' : 'Concours Financiers et Prêts';
    }
    return label;
  };

  const formatCurrency = (amount: number, currency: string) => {
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : 'FC';
    return `${symbol} ${amount.toLocaleString()}`;
  };

  const renderBankAccountForm = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Nom de la banque *
        </label>
        <input
          type="text"
          value={newItem.bankName || ''}
          onChange={(e) => setNewItem({ ...newItem, bankName: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
          placeholder="Ex: BCDC, TMB, etc."
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Intitulé du compte
        </label>
        <input
          type="text"
          value={newItem.accountTitle || ''}
          onChange={(e) => setNewItem({ ...newItem, accountTitle: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
          placeholder="Ex: Compte principal entreprise"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Numéro de compte *
        </label>
        <input
          type="text"
          value={newItem.accountNumber || ''}
          onChange={(e) => setNewItem({ ...newItem, accountNumber: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
          placeholder="Numéro de compte"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Devise
        </label>
        <select
          value={newItem.currency || 'USD'}
          onChange={(e) => setNewItem({ ...newItem, currency: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
        >
          {currencies.map(curr => (
            <option key={curr.value} value={curr.value}>{curr.label}</option>
          ))}
        </select>
      </div>
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Type de compte
        </label>
        <select
          value={newItem.accountType || 'business'}
          onChange={(e) => setNewItem({ ...newItem, accountType: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
        >
          {accountTypes.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderLoanForm = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Objet du prêt *
        </label>
        <input
          type="text"
          value={newItem.purpose || ''}
          onChange={(e) => setNewItem({ ...newItem, purpose: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
          placeholder="Ex: Équipement, Fonds de roulement"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Prêteur
        </label>
        <input
          type="text"
          value={newItem.lender || ''}
          onChange={(e) => setNewItem({ ...newItem, lender: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
          placeholder="Nom de la banque ou institution"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Montant total *
        </label>
        <input
          type="number"
          value={newItem.totalAmount || ''}
          onChange={(e) => setNewItem({ ...newItem, totalAmount: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
          placeholder="0"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Encours (montant restant)
        </label>
        <input
          type="number"
          value={newItem.outstandingAmount || ''}
          onChange={(e) => setNewItem({ ...newItem, outstandingAmount: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
          placeholder="0"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Taux d'intérêt (%)
        </label>
        <input
          type="number"
          step="0.01"
          value={newItem.interestRate || ''}
          onChange={(e) => setNewItem({ ...newItem, interestRate: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
          placeholder="0.00"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Devise
        </label>
        <select
          value={newItem.currency || 'USD'}
          onChange={(e) => setNewItem({ ...newItem, currency: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
        >
          {currencies.map(curr => (
            <option key={curr.value} value={curr.value}>{curr.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Date de début
        </label>
        <input
          type="date"
          value={newItem.startDate || ''}
          onChange={(e) => setNewItem({ ...newItem, startDate: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Date de fin
        </label>
        <input
          type="date"
          value={newItem.endDate || ''}
          onChange={(e) => setNewItem({ ...newItem, endDate: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
        />
      </div>
    </div>
  );

  const renderFundingRoundForm = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Type de tour *
        </label>
        <select
          value={newItem.roundType || ''}
          onChange={(e) => setNewItem({ ...newItem, roundType: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
        >
          <option value="">Sélectionner un type</option>
          {roundTypes.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Montant levé *
        </label>
        <input
          type="number"
          value={newItem.amountRaised || ''}
          onChange={(e) => setNewItem({ ...newItem, amountRaised: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
          placeholder="0"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Valorisation pré-money
        </label>
        <input
          type="number"
          value={newItem.preMoney || ''}
          onChange={(e) => setNewItem({ ...newItem, preMoney: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
          placeholder="0"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Valorisation post-money
        </label>
        <input
          type="number"
          value={newItem.postMoney || ''}
          onChange={(e) => setNewItem({ ...newItem, postMoney: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
          placeholder="0"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Devise
        </label>
        <select
          value={newItem.currency || 'USD'}
          onChange={(e) => setNewItem({ ...newItem, currency: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
        >
          {currencies.map(curr => (
            <option key={curr.value} value={curr.value}>{curr.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Date du tour
        </label>
        <input
          type="date"
          value={newItem.date || ''}
          onChange={(e) => setNewItem({ ...newItem, date: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
        />
      </div>
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Investisseurs (séparés par des virgules)
        </label>
        <input
          type="text"
          value={newItem.investors || ''}
          onChange={(e) => setNewItem({ ...newItem, investors: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
          placeholder="Ex: Angel Investor A, VC Fund B, Strategic Partner C"
        />
      </div>
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Utilisation des fonds
        </label>
        <textarea
          value={newItem.useOfFunds || ''}
          onChange={(e) => setNewItem({ ...newItem, useOfFunds: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent"
          placeholder="Décrivez comment les fonds ont été ou seront utilisés..."
        />
      </div>
    </div>
  );

  const renderItem = (item: any) => {
    if (type === 'bank-accounts') {
      const account = item as BankAccount;
      return (
        <div key={account.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <Building2 className="w-4 h-4 text-[#197ca8] mr-2" />
                <span className="font-medium text-gray-900">{account.bankName}</span>
                <span className="ml-2 px-2 py-1 bg-[#197ca8] text-white text-xs rounded">
                  {account.currency}
                </span>
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Compte:</strong> {account.accountNumber}</p>
                {account.accountTitle && <p><strong>Intitulé:</strong> {account.accountTitle}</p>}
                <p><strong>Type:</strong> {accountTypes.find(t => t.value === account.accountType)?.label}</p>
              </div>
            </div>
            {isEditing && (
              <button
                onClick={() => handleRemoveItem(account.id)}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      );
    } else if (type === 'loans') {
      const loan = item as Loan;
      return (
        <div key={loan.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <CreditCard className="w-4 h-4 text-[#197ca8] mr-2" />
                <span className="font-medium text-gray-900">{loan.purpose}</span>
                <span className="ml-2 px-2 py-1 bg-[#197ca8] text-white text-xs rounded">
                  {formatCurrency(loan.totalAmount, loan.currency)}
                </span>
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                {loan.lender && <p><strong>Prêteur:</strong> {loan.lender}</p>}
                <p><strong>Encours:</strong> {formatCurrency(loan.outstandingAmount, loan.currency)}</p>
                {loan.interestRate > 0 && <p><strong>Taux:</strong> {loan.interestRate}%</p>}
                {loan.startDate && <p><strong>Période:</strong> {loan.startDate} - {loan.endDate}</p>}
              </div>
            </div>
            {isEditing && (
              <button
                onClick={() => handleRemoveItem(loan.id)}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      );
    } else if (type === 'funding-rounds') {
      const round = item as FundingRound;
      return (
        <div key={round.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <TrendingUp className="w-4 h-4 text-[#197ca8] mr-2" />
                <span className="font-medium text-gray-900">
                  {roundTypes.find(t => t.value === round.roundType)?.label}
                </span>
                <span className="ml-2 px-2 py-1 bg-[#197ca8] text-white text-xs rounded">
                  {formatCurrency(round.amountRaised, round.currency)}
                </span>
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                {round.date && <p><strong>Date:</strong> {new Date(round.date).toLocaleDateString()}</p>}
                {round.preMoney > 0 && (
                  <p><strong>Valorisation:</strong> {formatCurrency(round.preMoney, round.currency)} (pré) / {formatCurrency(round.postMoney, round.currency)} (post)</p>
                )}
                {round.investors.length > 0 && (
                  <p><strong>Investisseurs:</strong> {round.investors.join(', ')}</p>
                )}
                {round.useOfFunds && (
                  <p><strong>Utilisation:</strong> {round.useOfFunds}</p>
                )}
              </div>
            </div>
            {isEditing && (
              <button
                onClick={() => handleRemoveItem(round.id)}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {getIcon()}
          <h3 className="text-lg font-medium text-gray-900">{getContextualLabel()}</h3>
          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
            {items.length} élément{items.length !== 1 ? 's' : ''}
          </span>
        </div>

        {!disabled && (
          <div className="flex items-center space-x-2">
            {!isEditing ? (
              <button
                onClick={handleStartEditing}
                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-[#197ca8] bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#197ca8]"
              >
                Gérer
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancelEditing}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#197ca8]"
                >
                  <X className="w-4 h-4 mr-1" />
                  Annuler
                </button>
                <button
                  onClick={handleSaveEditing}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Sauvegarder
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Liste des éléments */}
      {items.length > 0 ? (
        <div className="space-y-3">
          {items.map(renderItem)}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <Briefcase className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p>Aucun élément ajouté</p>
        </div>
      )}

      {/* Formulaire d'ajout */}
      {isEditing && (
        <div className="border-t border-gray-200 pt-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center mb-4">
              <Plus className="w-5 h-5 text-[#197ca8] mr-2" />
              <h4 className="text-md font-medium text-gray-900">
                Ajouter {type === 'bank-accounts' ? 'un compte bancaire' : 
                        type === 'loans' ? 'un prêt' : 'un tour de financement'}
              </h4>
            </div>

            {type === 'bank-accounts' && renderBankAccountForm()}
            {type === 'loans' && renderLoanForm()}
            {type === 'funding-rounds' && renderFundingRoundForm()}

            <div className="mt-4 flex justify-end">
              <button
                onClick={handleAddItem}
                disabled={
                  (type === 'bank-accounts' && (!newItem.bankName || !newItem.accountNumber)) ||
                  (type === 'loans' && (!newItem.purpose || !newItem.totalAmount)) ||
                  (type === 'funding-rounds' && (!newItem.roundType || !newItem.amountRaised))
                }
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#197ca8] hover:bg-[#156a92] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#197ca8] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialInput;